import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { NavItem, NavLink } from 'reactstrap';
import { ApplicationRoutes, UserRoles } from '../../constants';
import { useGeneralContext } from '../../services/GeneralContext';
import RolesHelper from '../Helpers/RolesHelper';
import Snow from '../ui/ee/Snow/Snow';
import NectarineButton from '../ui/NectarineButton/NectarineButton';
import AuthorizeService from './AuthorizeService';

interface LoginMenuProps {
  location?: string;
}

const LoginMenu: React.FC<LoginMenuProps> = ({ location }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [roles, setRoles] = useState<string[] | null>(null);
  const [userName, setUserName] = useState<string | null>(null);

  const { activeUserViewModel } = useGeneralContext();

  useEffect(() => {
    const subscription = AuthorizeService.subscribe(() => loadData());
    loadData();

    return () => {
      AuthorizeService.unsubscribe(subscription);
    };
  }, []);

  const loadData = async () => {
    const [authStatus, user] = await Promise.all([AuthorizeService.isAuthenticated(), AuthorizeService.getUser()]);
    const roles = await RolesHelper.getRoles();
    setIsAuthenticated(authStatus);
    setUserName(user?.name || null);
    setRoles(roles);
  };

  const authenticatedView = (userName: string, profilePath: string, logoutPath: string, logoutState: object) => {
    if (location === 'top') {
      return (
        <>
          {roles?.includes(UserRoles.Consumer) ? (
            <NavItem>
              <Snow>
                <div className="mt-2 me-2">
                  <NectarineButton
                    text="My Account"
                    color="white"
                    size="smedium"
                    href={`/${ApplicationRoutes.ClientAccountRoutes.Root}`}
                    hrefOpensNewTab={false}
                  />
                </div>
              </Snow>
            </NavItem>
          ) : (
            <></>
          )}

          {roles?.includes(UserRoles.Advisor) || (roles?.includes(UserRoles.Admin) && activeUserViewModel?.isNectarine) ? (
            <NavItem>
              <Snow>
                <div className="mt-2 me-2">
                  <NectarineButton
                    text="Advisor Home"
                    color="white"
                    size="smedium"
                    href={`/${ApplicationRoutes.AdvisorAccountRoutes.Root}`}
                    hrefOpensNewTab={false}
                  />
                </div>
              </Snow>
            </NavItem>
          ) : (
            <></>
          )}

          {roles?.includes(UserRoles.Admin) ? (
            <NavItem>
              <Snow>
                <div className="mt-2 me-2">
                  <NectarineButton
                    text="Admin"
                    color="white"
                    size="smedium"
                    href={`/${ApplicationRoutes.AdminRoutes.Root}`}
                    hrefOpensNewTab={false}
                  />
                </div>
              </Snow>
            </NavItem>
          ) : (
            <></>
          )}
        </>
      );
    }

    return (
      <>
        <NavItem>
          <NavLink tag={Link} to={profilePath}>
            Hello {userName}
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink replace tag={Link} to={logoutPath} state={logoutState}>
            Log Out
          </NavLink>
        </NavItem>
      </>
    );
  };

  const anonymousView = (registerPath: string, loginPath: string) => {
    if (location === 'top') {
      return (
        <NavItem>
          <div className="mt-2 me-3">
            <NectarineButton text="Log In" color="white" size="smedium" href={loginPath} hrefOpensNewTab={false} />
          </div>
        </NavItem>
      );
    }
    return (
      <>
        <NavItem>
          <NavLink tag={Link} to={registerPath}>
            Register
          </NavLink>
        </NavItem>

        <NavItem>
          <NavLink tag={Link} to={loginPath}>
            Log In
          </NavLink>
        </NavItem>
      </>
    );
  };

  const registerPath = `/${ApplicationRoutes.Register}`;
  const loginPath = `/${ApplicationRoutes.AuthenticationRoutes.Login}`;
  const profilePath = `/${ApplicationRoutes.AuthenticationRoutes.Profile}`;
  const logoutPath = `/${ApplicationRoutes.AuthenticationRoutes.Logout}`;
  const logoutState = { local: true };

  return isAuthenticated ? authenticatedView(userName || '', profilePath, logoutPath, logoutState) : anonymousView(registerPath, loginPath);
};

export default LoginMenu;
